import { Box, Paper, Popper, useMediaQuery, useTheme } from '@mui/material';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { forwardRef, KeyboardEvent, useCallback, useRef, useState } from 'react';
import { getDropdownPaperSx } from 'src/utils/dropdown-paper';
import { SelectMenu } from './select-menu';
import {
  FlyoutOption,
  OptionCheck,
  OptionCheckbox,
  OptionHint,
  OptionText,
  OptionTitleLabel,
  OptionTitleRow,
} from './select-menu.elements';
import { substringFilter } from './select-menu.filter';
import {
  GroupedSelectMenuOption,
  GroupedSelectMenuProps,
  SelectMenuOption,
} from './select-menu.types';

// Matches the base menu's `[cmdk-list]` cap so both columns scroll alike.
const FLYOUT_MAX_HEIGHT = '18.75rem';

const matchesQuery = (option: SelectMenuOption, query: string) =>
  substringFilter(
    option.value,
    query,
    [option.title, option.group ?? '', ...(option.keywords ?? [])].filter(Boolean),
  ) === 1;

/**
 * Grouped options on top of `SelectMenu`: group rows open their children in a
 * flyout (desktop) or drill in place with a back row (mobile), searching
 * flattens the tree into `Group › Label` rows, and checked rows sort to the
 * top on open. The base stays a flat list; everything grouped lives here.
 */
export const GroupedSelectMenu = forwardRef<HTMLButtonElement, GroupedSelectMenuProps>(
  (
    {
      options,
      value,
      defaultValue,
      onChange,
      multiple = false,
      values = [],
      onValuesChange,
      searchValue,
      onSearchChange,
      onGroupOpen,
      onOpenChange,
      menuWidth = '17rem',
      plainOptionIcons = false,
      defaultOpen = false,
      ...rest
    },
    forwardedRef,
  ) => {
    const theme = useTheme();
    // Mobile has no hover for the flyout: tapping a group drills into it in
    // place, with a back row on top. Reset on close.
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [open, setOpen] = useState(defaultOpen);
    const [internalValue, setInternalValue] = useState<string | null>(defaultValue ?? null);
    const [query, setQuery] = useState('');
    const [activeGroup, setActiveGroup] = useState<GroupedSelectMenuOption | null>(null);
    const [flyout, setFlyout] = useState<{
      option: GroupedSelectMenuOption;
      anchor: HTMLElement;
    } | null>(null);
    // Keyboard-active flyout row; -1 when the flyout was opened by hover.
    const [flyoutIndex, setFlyoutIndex] = useState(-1);
    const flyoutTimer = useRef<number | undefined>(undefined);
    // Timeout callbacks must read the live index — a hover-scheduled close can
    // still fire after ArrowUp/Down has moved flyoutIndex out of hover mode.
    const flyoutIndexRef = useRef(flyoutIndex);
    flyoutIndexRef.current = flyoutIndex;
    // Values snapshot from when the menu opened: checked rows sort to the top
    // on open, without live reordering while the user is still picking.
    const [sortValues, setSortValues] = useState(values);

    const currentValue = value !== undefined ? value : internalValue;
    const controlledSearch = Boolean(onSearchChange);
    const effectiveQuery = controlledSearch ? (searchValue ?? '') : query;

    const cancelFlyoutClose = () => window.clearTimeout(flyoutTimer.current);
    // Delayed so the pointer can travel from the group row into the flyout.
    // Keyboard-active flyouts (flyoutIndex >= 0) ignore stray hover-outs — a
    // parked pointer would otherwise close them on the first pixel of jiggle.
    const scheduleFlyoutClose = () => {
      cancelFlyoutClose();
      flyoutTimer.current = window.setTimeout(
        () => setFlyout((prev) => (flyoutIndexRef.current === -1 ? null : prev)),
        150,
      );
    };

    const clearSearch = () => {
      setQuery('');
      onSearchChange?.('');
    };

    const handleOpenChange = (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
      if (next) {
        setQuery('');
        setSortValues(values);
      } else {
        setFlyout(null);
        setActiveGroup(null);
      }
    };

    // Flyout rows bypass the base menu, so selection semantics live here too.
    const selectChild = useCallback(
      (child: SelectMenuOption) => {
        if (multiple) {
          const nextValues = values.includes(child.value)
            ? values.filter((entry) => entry !== child.value)
            : [...values, child.value];
          onValuesChange?.(nextValues, child);
          return;
        }
        if (value === undefined) {
          setInternalValue(child.value);
        }
        onChange?.(child.value, child);
        setFlyout(null);
        setOpen(false);
      },
      [multiple, values, onValuesChange, value, onChange],
    );

    const openGroup = (group: GroupedSelectMenuOption, anchor?: HTMLElement) => {
      onGroupOpen?.(group);
      if (mobile || !anchor) {
        clearSearch();
        setActiveGroup(group);
        return;
      }
      cancelFlyoutClose();
      setFlyout({ option: group, anchor });
      setFlyoutIndex(0);
    };

    const drillInto = (group: GroupedSelectMenuOption) => {
      openGroup(group);
    };

    const back = () => {
      clearSearch();
      setActiveGroup(null);
    };

    const isGroupOption = (option: GroupedSelectMenuOption) => option.children !== undefined;

    const groupRow = (group: GroupedSelectMenuOption): SelectMenuOption => {
      const checkedChildren = (group.children ?? []).filter((child) =>
        values.includes(child.value),
      );
      const checked = checkedChildren.length > 0;
      let hint;
      if (checked) {
        hint =
          checkedChildren.length === 1
            ? checkedChildren[0].title
            : `${checkedChildren.length} selected`;
      }

      return {
        ...group,
        onSelect: () => {
          if (mobile) {
            drillInto(group);
            return;
          }
          // Tap/click path for touch devices wider than `sm`, where hover never fires.
          const anchor = document.querySelector<HTMLElement>(
            `[cmdk-item][data-option-value="${CSS.escape(group.value)}"]`,
          );
          if (anchor) {
            openGroup(group, anchor);
          }
        },
        hint,
        adornment: multiple ? (
          // Unchecked group rows keep the (hidden) slot so every title aligns.
          <OptionCheckbox checked={checked} hideUnchecked />
        ) : undefined,
        endIcon: (
          <>
            {group.endIcon}
            <OptionCheck aria-hidden data-testid="select-menu-group-chevron">
              <ChevronRight />
            </OptionCheck>
          </>
        ),
      };
    };

    const searching = Boolean(effectiveQuery);
    const rank = (option: GroupedSelectMenuOption) => {
      const checked = isGroupOption(option)
        ? (option.children ?? []).some((child) => sortValues.includes(child.value))
        : sortValues.includes(option.value);
      return (checked ? 0 : 2) + (isGroupOption(option) ? 0 : 1);
    };

    // Re-resolve drilled/flyout groups from live `options` so server search and
    // load-more update the visible children instead of a drill-in snapshot.
    const liveActiveGroup = activeGroup
      ? options.find((option) => option.value === activeGroup.value)
      : undefined;
    const liveFlyoutGroup = flyout
      ? options.find((option) => option.value === flyout.option.value)
      : undefined;

    let rows: SelectMenuOption[];
    if (activeGroup) {
      // Drilled in (mobile): show the group's current children. Empty while a
      // per-group fetch is in flight or after a narrowing search drops matches.
      rows = liveActiveGroup?.children ?? [];
    } else if (searching) {
      // A query flattens grouped labels into `Group › Label` rows — children only.
      // Including the group row itself duplicated entries under server-backed search
      // (no local filter) and showed the group as an extra selectable.
      rows = options.flatMap((option) =>
        isGroupOption(option)
          ? (option.children ?? []).map((child) => ({ ...child, group: option.title }))
          : [option],
      );
    } else {
      // The hierarchy view sorts checked rows first, then groups, like Linear.
      rows = [...options]
        .sort((a, b) => rank(a) - rank(b))
        .map((option) => (isGroupOption(option) ? groupRow(option) : option));
    }
    if (!controlledSearch && effectiveQuery) {
      rows = rows.filter((row) => matchesQuery(row, effectiveQuery));
    }

    // `children` may be empty while a per-group fetch loads — still a group row.
    const sourceGroup = (optionValue: string | null | undefined) =>
      options.find((option) => option.value === optionValue && isGroupOption(option));

    const handleOptionHover = (option: SelectMenuOption, anchor: HTMLElement) => {
      const group = sourceGroup(option.value);
      if (group && !mobile) {
        onGroupOpen?.(group);
        cancelFlyoutClose();
        setFlyout({ option: group, anchor });
        setFlyoutIndex(-1);
      } else {
        scheduleFlyoutClose();
      }
    };

    const handleOptionHoverEnd = (option: SelectMenuOption) => {
      if (sourceGroup(option.value) && !mobile) {
        scheduleFlyoutClose();
      }
    };

    const handleKeyDownCapture = (event: KeyboardEvent<HTMLDivElement>) => {
      const children = flyout?.option.children ?? [];
      if (flyout && children.length) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          event.stopPropagation();
          const delta = event.key === 'ArrowDown' ? 1 : -1;
          // From a hover-open (-1), ArrowUp starts at the last row.
          setFlyoutIndex((prev) =>
            prev === -1 && delta === -1
              ? children.length - 1
              : (prev + delta + children.length) % children.length,
          );
          return;
        }
        if (event.key === 'Enter' && flyoutIndex >= 0) {
          event.preventDefault();
          event.stopPropagation();
          const child = children[flyoutIndex];
          if (child && !child.disabled) {
            selectChild(child);
          }
          return;
        }
        if (event.key === 'ArrowLeft' || event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          setFlyout(null);
        }
        return;
      }
      if (mobile && activeGroup && event.key === 'ArrowLeft') {
        event.preventDefault();
        event.stopPropagation();
        back();
        return;
      }
      if (!mobile && (event.key === 'ArrowRight' || event.key === 'Enter')) {
        const active = event.currentTarget.querySelector<HTMLElement>(
          '[cmdk-item][data-selected="true"]',
        );
        const group = sourceGroup(active?.getAttribute('data-option-value'));
        if (active && group) {
          event.preventDefault();
          event.stopPropagation();
          openGroup(group, active);
        }
      }
    };

    const selectedTitle = multiple
      ? undefined
      : options
          .flatMap((option) => option.children ?? [option])
          .find((option) => option.value === currentValue)?.title;

    return (
      <SelectMenu
        {...rest}
        ref={forwardedRef}
        options={rows}
        value={currentValue}
        onChange={(next, option) => {
          setInternalValue(next);
          onChange?.(next, option);
        }}
        multiple={multiple}
        values={values}
        onValuesChange={onValuesChange}
        triggerLabel={selectedTitle}
        menuWidth={menuWidth}
        plainOptionIcons={plainOptionIcons}
        open={open}
        onOpenChange={handleOpenChange}
        searchValue={effectiveQuery}
        onSearchChange={(next) => {
          // Typing changes the rows, so the flyout anchor can vanish.
          setFlyout(null);
          (onSearchChange ?? setQuery)(next);
        }}
        onOptionHover={handleOptionHover}
        onOptionHoverEnd={handleOptionHoverEnd}
        onKeyDownCapture={handleKeyDownCapture}
        header={
          activeGroup ? (
            <FlyoutOption
              data-testid="select-menu-back"
              onClick={back}
              sx={{
                width: '100%',
                borderRadius: 0,
                borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
              }}
            >
              <OptionHint>
                <ChevronLeft />
              </OptionHint>
              <OptionTitleRow>{liveActiveGroup?.title ?? activeGroup.title}</OptionTitleRow>
            </FlyoutOption>
          ) : (
            rest.header
          )
        }
      >
        <Popper
          open={Boolean(flyout)}
          anchorEl={flyout?.anchor}
          placement="right-start"
          sx={{ zIndex: 'modal' }}
          onMouseEnter={cancelFlyoutClose}
          onMouseLeave={scheduleFlyoutClose}
        >
          <Paper
            role="listbox"
            aria-label={liveFlyoutGroup?.title ?? flyout?.option.title}
            aria-multiselectable={multiple || undefined}
            sx={{
              ...getDropdownPaperSx(theme),
              width: menuWidth,
              padding: '0.375rem',
              // Same non-scrolling flex column as the base menu: the paper caps
              // its height and the rows below are the only scroll container, so
              // a long group stays reachable instead of running off-screen.
              display: 'flex',
              flexDirection: 'column',
              maxHeight: FLYOUT_MAX_HEIGHT,
              overflow: 'hidden',
            }}
          >
            <OptionHint
              sx={{
                // Full-bleed header band with the same divider as SearchRow.
                display: 'flex',
                flexShrink: 0,
                margin: '-0.375rem -0.375rem 0.375rem',
                padding: '0.5rem 0.9375rem',
                borderBottom: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
              }}
            >
              {liveFlyoutGroup?.title ?? flyout?.option.title}
            </OptionHint>
            <Box
              data-testid="select-menu-flyout-list"
              sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}
            >
              {(liveFlyoutGroup?.children ?? flyout?.option.children)?.map((child, index) => {
                const childChecked = multiple
                  ? values.includes(child.value)
                  : child.value === currentValue;
                return (
                  <FlyoutOption
                    key={child.value}
                    role="option"
                    aria-checked={multiple ? childChecked : undefined}
                    data-active={index === flyoutIndex || undefined}
                    disabled={child.disabled}
                    // Keep focus in the menu popover; a focus move would fight its focus trap.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectChild(child)}
                  >
                    {multiple ? <OptionCheckbox checked={childChecked} /> : null}
                    <OptionText>
                      <OptionTitleRow>
                        <OptionTitleLabel>{child.title}</OptionTitleLabel>
                        {child.endIcon}
                        {!multiple && childChecked ? (
                          <OptionCheck aria-hidden>
                            <Check />
                          </OptionCheck>
                        ) : null}
                      </OptionTitleRow>
                    </OptionText>
                  </FlyoutOption>
                );
              })}
            </Box>
          </Paper>
        </Popper>
      </SelectMenu>
    );
  },
);

GroupedSelectMenu.displayName = 'GroupedSelectMenu';
